import React, { useState, useEffect, useContext, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { SettingSalesManagerTab } from '@/components/organisms/settings/sales-manager/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { LoaderContextCreator } from '@/contexts/loader'
import {
  SalesSubjectTaxPriceType,
  SalesSubjectTaxRoundingType,
  SalesSubjectTaxRoundingUnit,
  SalesSubjectTaxType,
} from '@/models/sales-manager/sales'
import { EditFooter } from '@/components/organisms/edit-footer'
import * as api from '@/apis/aipass'
import { TableBody, TableHeader, TableRow } from '@/components/organisms/settings/sales-manager/table'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { SubjectTaxDetailModal } from '@/components/organisms/settings/sales-manager/subject-tax-detail-modal'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import { formatTaxApplicablePeriod } from '@/utils/sales-subject-tax'

export const SettingSalesManagerTax: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [salesSubjectTaxList, setSalesSubjectTaxList] = useState<SalesSubjectTaxType[]>([])
  const [showDetailProp, setShowDetailProp] = useState<SalesSubjectTaxType | null>()
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()

  const initializeComponent = async (opt?: { noIndicator: boolean }) => {
    !opt?.noIndicator && setIsLoading(true)
    const salesSubjectTaxList = await api.fetchSalesSubjectTax()
    setSalesSubjectTaxList([])
    setSalesSubjectTaxList(salesSubjectTaxList)
    setDeleteIds([])
    !opt?.noIndicator && setIsLoading(false)
  }

  const addDeleteTarget = (id: string) => {
    if (!guardSettingsEdit()) return
    setDeleteIds([...deleteIds, id])
  }

  const doDelete = async () => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        await api.deleteSalesSubSubjectTax(deleteIds)
        await initializeComponent()
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const doDeleteCancel = () => {
    setDeleteIds([])
  }

  const hasRoomChargeLimit = (detail: SalesSubjectTaxType): boolean => {
    return (
      detail.priceType === SalesSubjectTaxPriceType.FixRate && (detail.roomChargeUpperLimit != null || detail.roomChargeLowerLimit != null)
    )
  }

  const computedHasMultiLineRow = (detail: SalesSubjectTaxType): boolean => {
    return (detail.priceType === SalesSubjectTaxPriceType.FixAmount && detail.roomChargeRange.length > 1) || hasRoomChargeLimit(detail)
  }

  const makePriceDetail = (detail: SalesSubjectTaxType) => {
    if (detail.priceType === SalesSubjectTaxPriceType.FixRate) {
      const limitTexts: string[] = []
      if (detail.roomChargeUpperLimit != null) {
        limitTexts.push(t('saleSubjectTax.UpperLimitWithValue', { price: `¥${detail.roomChargeUpperLimit.toLocaleString()}` }))
      }
      if (detail.roomChargeLowerLimit != null) {
        limitTexts.push(t('saleSubjectTax.LowerLimitWithValue', { price: `¥${detail.roomChargeLowerLimit.toLocaleString()}` }))
      }
      return (
        <>
          <div>
            {t('saleSubjectTax.RateOfRoomCharge', {
              rate: Number(detail.roomChargeRate),
              unit: t(roundingUnitLabelKey[detail.roomChargeRoundingUnit]),
              rounding: t(roundingTypeLabelKey[detail.roomChargeRoundingType]),
            })}
          </div>
          {limitTexts.length > 0 && <div>{limitTexts.join(', ')}</div>}
        </>
      )
    } else {
      return detail.roomChargeRange.map((range, i) => {
        let priceRangeText = `¥${range.lowerPrice.toLocaleString()}~`
        if (range.upperPrice) {
          priceRangeText = `¥${range.lowerPrice.toLocaleString()}~¥${range.upperPrice.toLocaleString()}`
        }
        return (
          <div key={i}>
            {t('saleSubjectTax.FixOfRoomCharge', {
              range: priceRangeText,
              price: `¥${range.taxPrice.toLocaleString()}`,
            })}
          </div>
        )
      })
    }
  }

  useEffect(() => {
    initializeComponent()
  }, [])

  const isShowFooter = useMemo(() => deleteIds.length > 0, [deleteIds])
  const deleteFilteredSalesSubject = useMemo(() => salesSubjectTaxList.filter(detail => !deleteIds.includes(detail.id)), [
    salesSubjectTaxList,
    deleteIds,
  ])
  const footerContent = isShowFooter ? <EditFooter onSave={doDelete} onCancel={doDeleteCancel} /> : undefined

  return (
    <SettingsLayout loading={false} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingSalesManagerTab currentTab="saleSubjectTax.Tax" />} customStyle={taxTableStyle}>
        <>
          <div css={containerHeaderStyle}>
            <Button
              buttonType={isSettingsReadOnly ? 5 : 1}
              width={107}
              height={32}
              fontSize={12}
              isDisabled={isSettingsReadOnly}
              onClick={() => {
                if (!guardSettingsEdit()) return
                setShowDetailProp(null)
              }}
              icon={require('@/static/images/plus-white.svg')}
            >
              {t('Add new')}
            </Button>
          </div>
          <TableHeader className="table-header">
            <div className="name-col">{t('saleSubjectTax.Name')}</div>
            <div className="class-col">{t('saleSubjectTax.TargetChildClassification')}</div>
            <div className="break-col">{t('saleSubjectTax.PlanBreakSettings')}</div>
            <div className="price-col">{t('Amount')}</div>
            <div className="subject-col">{t('Subject')}</div>
            <div className="delete-col"></div>
          </TableHeader>
          <TableBody>
            {deleteFilteredSalesSubject.map(detail => (
              <TableRow
                className={`table-row ${computedHasMultiLineRow(detail) ? '-multi-line' : ''}`}
                css={css({
                  ...(computedHasMultiLineRow(detail)
                    ? {
                        alignItems: 'start',
                        // 定率の2行（料率 + 上限・下限）は定額と同じ余白だと上下が空きすぎるため半分にする。定額の複数行は従来どおり
                        padding: hasRoomChargeLimit(detail) ? '9px 0' : '18px 0',
                      }
                    : {}),
                })}
                key={detail.id}
                onClick={() => {
                  if (!guardSettingsEdit()) return
                  setShowDetailProp(detail)
                }}
              >
                <div className="name-col">
                  <div>{detail.name}</div>
                  <div className="period">
                    {t('saleSubjectTax.ApplicablePeriodWithValue', {
                      period: formatTaxApplicablePeriod(detail.validFrom, detail.validTo) ?? '—',
                      // 既定のエスケープでは日付の「/」が &#x2F; のまま表示される。テキストノードとして描画し React がエスケープするため無効化してよい
                      interpolation: { escapeValue: false },
                    })}
                  </div>
                </div>
                <div className="class-col">{detail.childrenClass.map(cl => t(`ChildrenClass.${cl}`)).join(', ') || '-'}</div>
                <div className="break-col">
                  {t(detail.isIncludeInReservationPrice ? 'saleSubjectTax.IncludedPrice' : 'saleSubjectTax.AdditionalCollection')}
                </div>
                <div className="price-col">{makePriceDetail(detail)}</div>
                <div className="subject-col">{detail.salesSubject.salesSubjectMasterName}</div>
                <div
                  className="delete-col"
                  style={{ marginTop: hasRoomChargeLimit(detail) ? '-5px' : computedHasMultiLineRow(detail) ? '-10px' : 'inherit' }}
                >
                  <DeleteIcon onClick={() => addDeleteTarget(detail.id)} />
                </div>
              </TableRow>
            ))}
          </TableBody>
          {showDetailProp !== undefined && (
            <SubjectTaxDetailModal
              detail={showDetailProp}
              onSave={() => {
                initializeComponent()
                setShowDetailProp(undefined)
              }}
              onCancel={() => setShowDetailProp(undefined)}
            />
          )}
        </>
      </TabContainer>
    </SettingsLayout>
  )
}

const roundingUnitLabelKey: Record<SalesSubjectTaxRoundingUnit, string> = {
  [SalesSubjectTaxRoundingUnit.Hundred]: 'saleSubjectTax.Hundred',
  [SalesSubjectTaxRoundingUnit.Thousand]: 'saleSubjectTax.Thousand',
}

const roundingTypeLabelKey: Record<SalesSubjectTaxRoundingType, string> = {
  [SalesSubjectTaxRoundingType.RoundDown]: 'saleSubjectTax.RoundDown',
  [SalesSubjectTaxRoundingType.RoundUp]: 'saleSubjectTax.RoundUp',
}

const containerHeaderStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '24px 32px 19px 32px',
  borderBottom: '1px solid #F2F2F2',
  '.subject-master-csv-uploader': { display: 'none' },
})

const taxTableStyle = css({
  '.table-row': {
    '> div': { fontWeight: 500 },
    minHeight: 49,
  },
  '.name-col': {
    marginLeft: 32,
    width: 186,
    '.period': { marginTop: 3, fontSize: 11, color: '#333333', whiteSpace: 'nowrap' },
  },
  '.class-col': { marginLeft: 32, width: 155 },
  '.break-col': { marginLeft: 32, width: 92 },
  '.price-col': { marginLeft: 32, flexGrow: 1 },
  '.subject-col': { marginLeft: 27, width: 128 },
  '.delete-col': { padding: '0 32px', width: 30 },
})
